import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { subDays, parseISO } from 'date-fns';
import { usePassportSummary } from '@/hooks/kpi/usePassportSummary';
import type { PassportTrendWeeklyResult } from '../../supabase/functions/_shared/kpiCanonical';
// DTC-J: route the rolling-N-day metrics window bounds through the
// canonical clock so analytics queries are deterministic under
// `clock.setFixed(...)`.
import { clock } from '@/utils/clock';

export interface DirectMetrics {
  jobs_completed: number;
  avg_efficiency_book: number | null;
  comeback_rate: number | null;
  diagnostic_accuracy: number | null;
}

export interface TrendDataPoint {
  date: string;
  efficiency: number;
  comebackRate: number;
  diagnosticAccuracy: number;
}

/**
 * K-T-7: routes through the canonical `kpi_passport_summary` RPC via
 * the Layer-3 `usePassportSummary` hook. Replaces the prior local
 * tickets + ticket_jobs queries and the four locally-computed KPIs.
 *
 * The RPC fans out across `technician_employment_history` so the
 * passport view stays cross-shop / lifetime — preserving the page's
 * intent. The `liveShopTechnicianId` parameter is preserved on the
 * signature for caller backward compatibility but no longer narrows
 * the math (the RPC handles the fan-out internally). Future
 * shop-scoped consumers should call `useTechnicianSummary` directly.
 *
 * Lifetime window is encoded as a wide ISO range (1900-01-01 through
 * 2099-12-31) so the canonical `[start_at, end_at)` half-open
 * contract still applies. Date-bounded passport views in the future
 * can be added by extending the hook's signature; current callers
 * are unchanged.
 *
 * Return shape: `{ data: DirectMetrics | undefined, isLoading,
 * isError, error }`. The `DirectMetrics` shape is preserved verbatim
 * — gated rate KPIs collapse to `null`, hours and jobs_completed
 * stay non-nullable. Callers (TechnicianPassport, PassportHome,
 * PassportPerformanceDNA) destructure only `data`, so the narrower
 * return shape is back-compat.
 */
export function useDirectTechnicianMetrics(
  technicianProfileId: string,
  // Preserved on the signature for caller compat; not consumed by
  // K-T-7 because `kpi_passport_summary` handles the employment-
  // history fan-out internally. Eliminating this parameter is a
  // future K-T-* cleanup.
   
  liveShopTechnicianId?: string,
) {
  const { data, isLoading, isError, error } = usePassportSummary({
    profileId: technicianProfileId,
    startAt: '1900-01-01T00:00:00Z',
    endAt: '2099-12-31T23:59:59.999Z',
  });

  // Map canonical PassportSummaryResult → legacy DirectMetrics shape.
  // Pre-K-T-7 callers used the same field names; only the SOURCE of
  // each value moved from local arithmetic to canonical SQL.
  const mapped: DirectMetrics | undefined = data
    ? {
        jobs_completed: data.jobs_completed,
        avg_efficiency_book: data.efficiency_gated ? null : data.efficiency_ratio,
        comeback_rate: data.comeback_gated ? null : data.comeback_rate,
        diagnostic_accuracy: data.diagnostic_gated
          ? null
          : data.diagnostic_accuracy,
      }
    : undefined;

  return { data: mapped, isLoading, isError, error };
}

/**
 * K-T-13-D: passport weekly trend now sourced from canonical
 * `kpi_passport_trend_weekly` RPC. Replaces the prior client-side
 * per-week reduce + Sun-anchored bucketing + SUM/SUM math.
 *
 * Behavior preserved:
 *   - Sun-anchored weeks computed in shop TZ (the SQL CASE mirrors
 *     the JS bucketing exactly).
 *   - Profile fan-out via `technician_employment_history` when
 *     `liveShopTechnicianId` is not provided; pinned to that single
 *     `shop_technician_id` when it is (`p_shop_tech_id` parameter on
 *     the RPC).
 *   - Rolling N-day window via `subDays(now, daysBack)` → `new Date()`.
 *   - Output shape: `{ date, efficiency, comebackRate, diagnosticAccuracy }`
 *     with integer-percent values (NOT fractions) so the chart's
 *     `dataKey="efficiency"` and `domain={[60, 140]}` continue to
 *     render unchanged.
 *   - `diagnosticAccuracy` stays `0` (deferred — per-bucket
 *     ticket_jobs join still not wired up; consistent with the
 *     pre-K-T-13-D placeholder).
 *
 * Behavior change:
 *   - Per-week ratio now applies `MIN_KPI_SAMPLE_SIZE = 3` sample
 *     gate. Weeks with < 3 qualifying tickets render as 0% (chart
 *     fallback), matching the pre-K-T-13-D `totalActual === 0`
 *     fallback. Pre-K-T-13-D such weeks rendered a real but noisy
 *     ratio (e.g. one ticket's ratio).
 */
export function useTechnicianTrendData(
  technicianProfileId: string,
  timeRange: '30d' | '90d',
  shopTimezone: string,
  liveShopTechnicianId?: string,
  /**
   * CORE-C GAP-005. `liveShopTechnicianId` is part of the query KEY, so a
   * caller that supplies it asynchronously makes this query run once with
   * the id absent and again once it arrives - a duplicate round trip whose
   * first result is discarded. A caller that resolves the live identity
   * asynchronously passes `false` until that lookup has SETTLED.
   *
   * Defaults to true so every existing caller is unaffected. This gates
   * WHEN the query runs and changes nothing about what it computes.
   */
  liveIdentityReady: boolean = true,
) {
  return useQuery({
    queryKey: ['direct-technician-trends', technicianProfileId, timeRange, shopTimezone, liveShopTechnicianId],
    queryFn: async (): Promise<TrendDataPoint[]> => {
      const daysBack = timeRange === '30d' ? 30 : 90;
      const startAt = subDays(clock.now(), daysBack).toISOString();
      const endAt = clock.now().toISOString();

      const { data, error } = await (supabase.rpc as unknown as (
        fn: string,
        args: {
          p_profile_id: string;
          p_start_at: string;
          p_end_at: string;
          p_shop_timezone: string;
          p_shop_tech_id: string | null;
        },
      ) => Promise<{
        data: PassportTrendWeeklyResult[] | null;
        error: unknown;
      }>)('kpi_passport_trend_weekly', {
        p_profile_id: technicianProfileId,
        p_start_at: startAt,
        p_end_at: endAt,
        p_shop_timezone: shopTimezone,
        p_shop_tech_id: liveShopTechnicianId ?? null,
      });

      if (error) throw error;
      if (!data || data.length === 0) return [];

      return data.map((row) => ({
        // `week_start` is the Sun-anchored `yyyy-MM-dd` from the RPC.
        // Render in browser locale to match the pre-K-T-13-D
        // `parseISO(dateKey + 'T00:00:00').toLocaleDateString(...)` label.
        date: parseISO(row.week_start + 'T00:00:00').toLocaleDateString(
          'en-US',
          { month: 'short', day: 'numeric' },
        ),
        // Gated weeks → 0 (matches pre-K-T-13-D `totalActual === 0`
        // fallback so the chart's domain={[60, 140]} doesn't see a
        // negative or fractional value).
        efficiency: row.gated || row.ratio === null
          ? 0
          : Math.round(row.ratio * 100),
        // comeback_rate is NOT gated. NULL fallback to 0% (defensive —
        // per-week rows always have jobs_completed >= 1, so this is a
        // theoretical branch only).
        comebackRate: Math.round((row.comeback_rate ?? 0) * 100),
        // Per-week diagnostic accuracy still deferred (would require a
        // per-bucket ticket_jobs join with diagnosis_outcome).
        diagnosticAccuracy: 0,
      }));
    },
    enabled: !!technicianProfileId && liveIdentityReady,
  });
}
